import React, { useState, useEffect } from "react";
import {
  addAddressIcon,
  addListIcon,
  arrowDownIcon,
  arrowUpIcon,
  assuredIcon,
  branchIcon,
  contractIcon,
  dashboardIcon,
  firmIcon,
  galleryIcon,
  helpCategoryIcon,
  helpCenterIcon,
  helpTopicsIcon,
  HsnSacIcon,
  labProfileIcon,
  listAltCheckIcon,
  localActivityIcon,
  lookupICon,
  manageIcon,
  measurementFieldIcon,
  measurementTypeIcon,
  passkeyIcon,
  receiptLongIcon,
  settingIcon,
  subscriptionIcon,
  taxationIcon,
  twoPagerPaIcon,
} from "../../../assets/icons/SvgIconList";
import { Link, useLocation } from "react-router-dom";
import UseTranslationHook from "../../../hooks/UseTranslationHook";
import { cn } from "../../../lib";
import { routeAdmin } from "../../../routes/AuthRoutePath";
import ICImage from "../../../core-component/ui/ICImage";

interface SubItem {
  key: string;
  label: string;
  icon?: React.ReactNode;
  url: string;
}

interface SideBarItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  url: string;
  subItems?: SubItem[];
}

const Sidebar = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>(
    {}
  );
  const { t } = UseTranslationHook();
  const location = useLocation();
  const heading = location?.pathname?.replace("/admin/", "");

  const handleMouseEnter = () => {
    !isOpen && setIsHovered(true);
  };

  const handleMouseLeave = () => {
    !isOpen && setIsHovered(false);
  };

  const toggleDropdown = (key: string) => {
    setOpenDropdowns((prevState) => ({
      ...prevState,
      [key]: !prevState[key],
    }));
  };

  const sideBarItems = [
    {
      key: "dashboard",
      label: "dashboard",
      icon: dashboardIcon,
      url: routeAdmin.dashboard,
    },
    {
      key: "lookup",
      label: "lookup",
      icon: lookupICon,
      url: routeAdmin.lookup,
    },
    { key: "firm", label: "firm", icon: firmIcon, url: routeAdmin.firm },
    {
      key: "address",
      label: "firm_address",
      icon: addAddressIcon,
      url: routeAdmin.address,
    },
    {
      key: "firm-purchased-subscription",
      label: "firm_purchased_subscription",
      icon: localActivityIcon,
      url: routeAdmin.firmPurchasedSubscription,
    },
    {
      key: "branch",
      label: "branch",
      icon: branchIcon,
      url: routeAdmin.branch,
    },
    {
      key: "measurement-field",
      label: "measurement_field",
      icon: measurementFieldIcon,
      url: routeAdmin.measurementField,
    },
    {
      key: "measurement-type",
      label: "measurement_type",
      icon: measurementTypeIcon,
      url: routeAdmin.measurementType,
    },
    {
      key: "gallery-directory",
      label: "Gallery Directory",
      icon: galleryIcon,
      url: routeAdmin.galleryDirectory,
    },
    {
      key: "taxation",
      label: "taxation",
      icon: taxationIcon,
      url: "#",
      subItems: [
        {
          key: "taxation-hsn-sac-code",
          label: "hsn_sac_code",
          icon: HsnSacIcon,
          url: routeAdmin.hsnSacCode,
        },
        {
          key: "taxation-tax-category",
          label: "category",
          icon: addListIcon,
          url: routeAdmin.taxCategory,
        },
        {
          key: "taxation-tax-components",
          label: "components",
          icon: contractIcon,
          url: routeAdmin.taxComponents,
        },
        {
          key: "taxation-tax-groups",
          label: "groups",
          icon: labProfileIcon,
          url: routeAdmin.taxGroups,
        },
      ],
    },
    {
      key: "role-permission",
      label: "role_permission",
      icon: passkeyIcon,
      url: "#",
      subItems: [
        {
          key: "claim-group",
          label: "claim_group",
          icon: twoPagerPaIcon,
          url: routeAdmin.claimGroup,
        },
        {
          key: "roles",
          label: "roles",
          icon: listAltCheckIcon,
          url: routeAdmin.roles,
        },
      ],
    },

    {
      key: "subscription-package",
      label: "subscription_package",
      icon: subscriptionIcon,
      url: routeAdmin.subscriptionPackage,
    },

    {
      key: "payment-transaction",
      label: "payment_transaction",
      icon: receiptLongIcon,
      url: routeAdmin.paymentTransaction,
    },
    {
      key: "expenses",
      label: "expenses",
      icon: assuredIcon,
      url: routeAdmin.expenses,
    },

    {
      key: "help-center",
      label: "help_center",
      icon: helpCenterIcon,
      url: "#",
      subItems: [
        {
          key: "help-category",
          label: "help_category",
          icon: helpCategoryIcon,
          url: routeAdmin.helpCategory,
        },
        {
          key: "help-topics",
          label: "help_topics",
          icon: helpTopicsIcon,
          url: routeAdmin.helpTopics,
        },
      ],
    },

    {
      key: "system-setting",
      label: "system_setting",
      icon: settingIcon,
      url: routeAdmin.systemConfiguration,
    },
    {
      key: "content-management",
      label: "content_management",
      icon: manageIcon,
      url: routeAdmin.contentManagement,
    },
  ];
  const isSubItemMatch = (item: SideBarItem, heading: string) => {
    return item.subItems?.some((subItem) => heading === subItem.url) ?? false;
  };
  const updateDropdownState = (item: SideBarItem) => {
    setOpenDropdowns((prevState) => ({
      ...prevState,
      [item.key]: true,
    }));
  };

  useEffect(() => {
    const updateOpenDropdowns = () => {
      sideBarItems.forEach((item) => {
        if (isSubItemMatch(item, heading)) {
          updateDropdownState(item);
        }
      });
    };

    updateOpenDropdowns();
  }, []);

  return (
    <div className="flex min-w-16 border-r border-base">
      <button
        className={cn(
          "h-svh flex flex-col z-10 duration-300 ease-in-out bg-subtle overflow-hidden",
          {
            "w-16": !isOpen,
            "w-64": isOpen,
            "!w-64 fixed left-0": !isOpen && isHovered,
            "fixed inset-y-0 left-0": !isOpen && !isHovered,
          }
        )}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Sidebar header */}
        <div className="flex items-center justify-between h-16 border-b border-base p-4 w-full">
          <ICImage
            src={require("../../../assets/image/app-favicon.webp")}
            containerClass="!w-7"
            alt={t("logo")}
          />
          {(isHovered || isOpen) && (
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="cursor-pointer"
            >
              <div className="h-4 w-4 border-2 border-accent rounded-full grid place-items-center">
                {isOpen && (
                  <div className="bg-accent-emphasis rounded-full h-1/2 w-1/2" />
                )}
              </div>
            </button>
          )}
        </div>

        <nav className="flex-1 overflow-y-scroll">
          <ul className="p-2 space-y-2">
            {sideBarItems.map((item) => (
              <li key={item.key}>
                <button
                  className={cn("flex items-center rounded-md w-full", {
                    "bg-accent-emphasis text-on-accent": heading === item.url,
                    "hover:bg-base": heading !== item.url,
                  })}
                  onClick={
                    item.subItems ? () => toggleDropdown(item.key) : undefined
                  }
                >
                  <Link
                    to={item.url}
                    className="py-2 px-3 flex items-center w-full"
                  >
                    <div className="size-6 shrink-0">{item.icon}</div>
                    {(isHovered || isOpen) && (
                      <span className="ml-4 font-medium">{t(item.label)}</span>
                    )}
                  </Link>
                  {item.subItems && (isHovered || isOpen) && (
                    <div className="ml-auto mr-4">
                      {openDropdowns[item.key] ? (
                        <div className="size-5">{arrowUpIcon}</div>
                      ) : (
                        <div className="size-5">{arrowDownIcon}</div>
                      )}
                    </div>
                  )}
                </button>

                {openDropdowns[item.key] &&
                  item.subItems &&
                  (isHovered || isOpen) && (
                    <ul>
                      {item.subItems.map((subItem) => (
                        <li
                          key={subItem.key}
                          className={cn("rounded-md ", {
                            "bg-accent-emphasis text-on-accent":
                              heading === subItem.url,
                            "hover:bg-base": heading !== subItem.url,
                          })}
                        >
                          <Link
                            to={subItem.url}
                            className="py-2 px-3 flex items-center w-full mt-2"
                          >
                            <div className="size-6 flex items-center justify-center shrink-0">
                              <div
                                className={cn(
                                  "size-3 rounded-full border-2 border-black",
                                  {
                                    "border-base": heading === subItem.url,
                                  }
                                )}
                              ></div>
                            </div>
                            {subItem.icon && (
                              <div className="size-6 ml-4">{subItem.icon}</div>
                            )}
                            <span className={cn("font-medium ml-4")}>
                              {t(subItem.label)}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
              </li>
            ))}
          </ul>
        </nav>
      </button>
    </div>
  );
};

export default Sidebar;
